/**
 * Contracts Breakdown Card
 *
 * Shows individual contract details when an agent has multiple contracts.
 * Clean, compact design using brand-consistent colors.
 *
 * PRINCIPLES:
 * - Clean, non-cluttered UI
 * - Each contract is a compact row with key metrics
 * - Collapsible when 3+ contracts for tidiness
 * - Client-friendly language throughout
 * - Brand colors: navy/gold/cream palette
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, FileText, CheckCircle2, Clock, AlertTriangle, PauseCircle, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { format } from "date-fns";
import type { ContractType, ContractStatus } from "@/contexts/ContractsContext";
import { getCycleDays, getPlanLabel } from "@/lib/planDisplay";

export interface ContractBreakdownItem {
  id: string;
  contractType: ContractType;
  totalLeadsCommitted: number;
  leadsDelivered: number;
  cycleStart: number;
  cycleEnd: number;
  status: ContractStatus;
}

interface ContractsBreakdownCardProps {
  contracts: ContractBreakdownItem[];
}

const statusConfig: Record<string, {
  label: string;
  icon: typeof CheckCircle2;
  variant: "default" | "secondary" | "outline" | "destructive";
}> = {
  active: {
    label: "Active",
    icon: Clock,
    variant: "default",
  },
  at_risk: {
    label: "Needs Attention",
    icon: AlertTriangle,
    variant: "secondary",
  },
  fulfilled: {
    label: "Complete",
    icon: CheckCircle2,
    variant: "default",
  },
  expired: {
    label: "Expired",
    icon: Clock,
    variant: "outline",
  },
  breached: {
    label: "Expired",
    icon: AlertTriangle,
    variant: "outline",
  },
  paused: {
    label: "Paused",
    icon: PauseCircle,
    variant: "secondary",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    variant: "outline",
  },
};

function ContractRow({ contract }: { contract: ContractBreakdownItem }) {
  const remaining = Math.max(0, contract.totalLeadsCommitted - contract.leadsDelivered);
  const progressPercent = contract.totalLeadsCommitted > 0
    ? Math.min(100, (contract.leadsDelivered / contract.totalLeadsCommitted) * 100)
    : 0;

  const config = statusConfig[contract.status] || statusConfig.active;
  const cycleDays = getCycleDays(contract.cycleStart, contract.cycleEnd);
  const { label } = getPlanLabel(contract.contractType, cycleDays);

  return (
    <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-2.5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {label}
          </span>
        </div>
        <Badge variant={config.variant} className="text-xs">
          {config.label}
        </Badge>
      </div>

      {/* Progress */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{contract.leadsDelivered} of {contract.totalLeadsCommitted} delivered</span>
          <span>{remaining} remaining</span>
        </div>
        <Progress value={progressPercent} className="h-1.5" />
      </div>

      {/* Date range */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {format(new Date(contract.cycleStart), "MMM d")} – {format(new Date(contract.cycleEnd), "MMM d, yyyy")}
        </span>
        <span className="font-medium text-foreground">{Math.round(progressPercent)}%</span>
      </div>
    </div>
  );
}

export function ContractsBreakdownCard({ contracts }: ContractsBreakdownCardProps) {
  const [isExpanded, setIsExpanded] = useState(contracts.length <= 3);

  if (contracts.length === 0) return null;
  if (contracts.length === 1) return null; // Single contract — no need for breakdown

  const activeContracts = contracts.filter(c => c.status === 'active' || c.status === 'at_risk');
  const completedContracts = contracts.filter(c => c.status === 'fulfilled' || c.status === 'expired' || c.status === 'breached');

  const visibleContracts = isExpanded ? contracts : contracts.slice(0, 2);
  const hasMore = contracts.length > 2 && !isExpanded;

  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-base font-semibold text-foreground">Your Contracts</h3>
          <Badge variant="secondary" className="text-xs">
            {activeContracts.length} active{completedContracts.length > 0 ? `, ${completedContracts.length} completed` : ""}
          </Badge>
        </div>
        {contracts.length > 2 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {isExpanded ? (
              <>Show less <ChevronUp className="w-3 h-3" /></>
            ) : (
              <>Show all ({contracts.length}) <ChevronDown className="w-3 h-3" /></>
            )}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {visibleContracts.map((contract) => (
          <ContractRow key={contract.id} contract={contract} />
        ))}
        {hasMore && (
          <button
            onClick={() => setIsExpanded(true)}
            className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors text-center border border-dashed border-border rounded-lg"
          >
            + {contracts.length - 2} more contracts
          </button>
        )}
      </div>
    </div>
  );
}
